import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCw,
  Trash2,
  UserCheck,
  UserX,
  XCircle,
} from 'lucide-react';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { AccountDeletionRequest, AccountDeletionStats, DeactivatedAccount } from '../Types/AccountDeletionType';
import {
  approveAccountDeletionRequest,
  getAccountDeletionRequests,
  getAccountDeletionStats,
  getDeactivatedAccounts,
  reactivateDeactivatedAccount,
  rejectAccountDeletionRequest,
} from '../Services/AccountDeletionAdminService';

const PAGE_SIZE = 10;
const CONFIRM_PHRASE = 'DELETE';

type Tab = 'PENDING' | 'APPROVED' | 'REJECTED' | 'ALL' | 'DEACTIVATED_CANDIDATES' | 'DEACTIVATED_RECRUITERS';

const TABS: { label: string; value: Tab }[] = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Rejected', value: 'REJECTED' },
  { label: 'All requests', value: 'ALL' },
  { label: 'Deactivated candidates', value: 'DEACTIVATED_CANDIDATES' },
  { label: 'Deactivated recruiters', value: 'DEACTIVATED_RECRUITERS' },
];

const REQUEST_TABS: Tab[] = ['PENDING', 'APPROVED', 'REJECTED', 'ALL'];
const isRequestTab = (tab: Tab) => REQUEST_TABS.includes(tab);

const ROLE_LABEL: Record<string, string> = {
  CANDIDATE: 'Candidate',
  RECRUITER: 'Recruiter',
  SUPER_ADMIN: 'Super Admin',
  DELEGATED_ADMIN: 'Delegated Admin',
};

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleString() : '—';

const daysOld = (value: string) => {
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  return days === 1 ? '1 day ago' : `${days} days ago`;
};

const emptyStats: AccountDeletionStats = {
  pendingRequests: 0,
  approvedRequests: 0,
  rejectedRequests: 0,
  oldestPendingRequestDays: null,
  deactivatedCandidates: 0,
  deactivatedRecruiters: 0,
  deactivatedCandidatesLast7Days: 0,
  deactivatedRecruitersLast7Days: 0,
  deactivatedCandidatesLast30Days: 0,
  deactivatedRecruitersLast30Days: 0,
};

interface StatCardProps {
  label: string;
  value: string | number;
  detail?: string;
  accent: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, detail, accent }) => (
  <div className="min-w-[9.5rem] flex-1 rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`mt-1.5 text-2xl font-black tracking-tight ${accent}`}>{value}</p>
    {detail && <p className="mt-0.5 text-[11px] text-slate-600">{detail}</p>}
  </div>
);

interface PagerProps {
  page: number;
  totalPages: number;
  totalElements: number;
  onPage: (page: number) => void;
  disabled: boolean;
}

const Pager: React.FC<PagerProps> = ({ page, totalPages, totalElements, onPage, disabled }) => {
  const safeTotal = Math.max(1, totalPages);
  return (
    <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
      <p className="text-xs text-slate-500">
        {totalElements} total · page {page + 1} of {safeTotal}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onPage(page - 1)}
          disabled={disabled || page <= 0}
          className="rounded-xl border border-slate-800 bg-slate-900 px-4 py-2 text-xs font-black uppercase tracking-widest text-slate-300 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={() => onPage(page + 1)}
          disabled={disabled || page + 1 >= safeTotal}
          className="rounded-xl border border-slate-800 bg-slate-900 px-4 py-2 text-xs font-black uppercase tracking-widest text-slate-300 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
};

/**
 * SUPER_ADMIN account lifecycle surface.
 *
 * <p>Two kinds of thing live here. Deletion requests, which are acted on once and then
 * become history - approving is irreversible, so it is gated behind typing a confirmation
 * phrase rather than a single click. And deactivated accounts, which are simply switched
 * off and can be restored at any time.</p>
 */
const AccountDeletionRequestsPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('PENDING');
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [requests, setRequests] = useState<AccountDeletionRequest[]>([]);
  const [deactivated, setDeactivated] = useState<DeactivatedAccount[]>([]);
  const [stats, setStats] = useState<AccountDeletionStats>(emptyStats);

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [confirming, setConfirming] = useState<AccountDeletionRequest | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [rejecting, setRejecting] = useState<AccountDeletionRequest | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [restoring, setRestoring] = useState<DeactivatedAccount | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const fail = (message: string) =>
    setAlert({
      variant: 'error',
      title: 'Something went wrong',
      message,
      autoCloseMs: 8000,
      onClose: () => setAlert(null),
    });

  const succeed = (message: string) =>
    setAlert({
      variant: 'success',
      title: 'Done',
      message,
      autoCloseMs: 5000,
      onClose: () => setAlert(null),
    });

  const loadStats = useCallback(async () => {
    const response = await getAccountDeletionStats();
    if (response.success && response.data) {
      setStats(response.data);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);

    if (isRequestTab(tab)) {
      const response = await getAccountDeletionRequests(
        tab === 'ALL' ? undefined : tab,
        page,
        PAGE_SIZE,
      );

      if (response.success) {
        setRequests(response.data?.content ?? []);
        setTotalElements(response.data?.totalElements ?? response.data?.content?.length ?? 0);
        setTotalPages(response.data?.totalPages ?? 1);
      } else {
        fail(response.message);
      }
    } else {
      const role = tab === 'DEACTIVATED_CANDIDATES' ? 'CANDIDATE' : 'RECRUITER';
      const response = await getDeactivatedAccounts(role, page, PAGE_SIZE);

      if (response.success) {
        setDeactivated(response.data?.content ?? []);
        setTotalElements(response.data?.totalElements ?? response.data?.content?.length ?? 0);
        setTotalPages(response.data?.totalPages ?? 1);
      } else {
        fail(response.message);
      }
    }

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const selectTab = (next: Tab) => {
    setTab(next);
    setPage(0);
  };

  const refresh = () => {
    load();
    loadStats();
  };

  const closeModals = () => {
    setConfirming(null);
    setRejecting(null);
    setRestoring(null);
    setConfirmText('');
    setRejectNote('');
  };

  const run = async (id: string, action: () => Promise<any>, successMessage: string) => {
    setBusyId(id);
    const response = await action();
    setBusyId(null);

    if (response.success) {
      closeModals();
      succeed(successMessage);
      refresh();
      return;
    }

    fail(response.message);
  };

  const pendingTabCount = stats.pendingRequests;
  const showEmpty = !loading && (isRequestTab(tab) ? requests.length === 0 : deactivated.length === 0);

  return (
    <div className="min-h-screen bg-slate-950 p-4 sm:p-6 lg:p-8">
      {alert && (
        <div className="mb-4">
          <Alert
            variant={alert.variant}
            title={alert.title}
            message={alert.message}
            onClose={() => setAlert(null)}
          />
        </div>
      )}

      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-100">
              Account Lifecycle
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-400">
              Users cannot permanently delete their own account. They deactivate it, or ask for
              deletion with a reason — and approval is the only irreversible step here.
            </p>
          </div>

          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-300 transition hover:bg-slate-800 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* Analytics */}
        <div className="mt-6 flex flex-wrap gap-3">
          <StatCard
            label="Pending requests"
            value={stats.pendingRequests}
            detail={
              stats.oldestPendingRequestDays != null
                ? `oldest waiting ${stats.oldestPendingRequestDays}d`
                : 'nothing waiting'
            }
            accent="text-amber-300"
          />
          <StatCard label="Approved" value={stats.approvedRequests} detail="accounts removed" accent="text-rose-300" />
          <StatCard label="Rejected" value={stats.rejectedRequests} detail="accounts restored" accent="text-emerald-300" />
          <StatCard
            label="Deactivated candidates"
            value={stats.deactivatedCandidates}
            detail={`${stats.deactivatedCandidatesLast7Days} this week · ${stats.deactivatedCandidatesLast30Days} this month`}
            accent="text-cyan-300"
          />
          <StatCard
            label="Deactivated recruiters"
            value={stats.deactivatedRecruiters}
            detail={`${stats.deactivatedRecruitersLast7Days} this week · ${stats.deactivatedRecruitersLast30Days} this month`}
            accent="text-fuchsia-300"
          />
        </div>

        {/* Tabs */}
        <div className="mt-6 flex flex-wrap gap-2">
          {TABS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => selectTab(option.value)}
              className={`rounded-xl px-4 py-2 text-xs font-black uppercase tracking-widest transition ${
                tab === option.value
                  ? 'bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-400/40'
                  : 'bg-slate-900 text-slate-500 hover:text-slate-300'
              }`}
            >
              {option.label}
              {option.value === 'PENDING' && pendingTabCount > 0 && (
                <span className="ml-2 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] text-amber-300">
                  {pendingTabCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="mt-6 space-y-4">
          {loading && (
            <div className="flex items-center justify-center gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 py-16 text-slate-400">
              <Loader2 size={18} className="animate-spin" />
              <span className="text-sm font-semibold">Loading…</span>
            </div>
          )}

          {showEmpty && (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 py-16 text-center">
              <CheckCircle2 size={28} className="text-emerald-400" />
              <p className="text-sm font-semibold text-slate-300">Nothing to show here.</p>
            </div>
          )}

          {isRequestTab(tab) &&
            requests.map((request) => (
              <div
                key={request.id}
                className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5 shadow-xl"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-black text-slate-100">
                        {request.fullName || 'Unnamed user'}
                      </span>
                      <span className="rounded-full bg-slate-800 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        {ROLE_LABEL[request.role] || request.role}
                      </span>
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                          request.status === 'PENDING'
                            ? 'bg-amber-500/15 text-amber-300'
                            : request.status === 'APPROVED'
                            ? 'bg-rose-500/15 text-rose-300'
                            : 'bg-emerald-500/15 text-emerald-300'
                        }`}
                      >
                        {request.status}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-xs text-slate-500">{request.email}</p>
                  </div>

                  <div className="text-right text-xs text-slate-500">
                    <p className="flex items-center justify-end gap-1.5">
                      <Clock size={12} />
                      {formatDate(request.requestedAt)}
                    </p>
                    <p className="mt-1">{daysOld(request.requestedAt)}</p>
                  </div>
                </div>

                <div className="mt-4 rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                    Reason given
                  </p>
                  <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-300">
                    {request.reason}
                  </p>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <UserX size={12} />
                    Account: {request.accountStillExists ? request.accountStatus || 'unknown' : 'already removed'}
                  </span>
                  {request.accountDeactivatedAt && (
                    <span>Deactivated {formatDate(request.accountDeactivatedAt)}</span>
                  )}
                  {request.reviewedAt && (
                    <span>
                      Reviewed {formatDate(request.reviewedAt)}
                      {request.reviewNote ? ` — ${request.reviewNote}` : ''}
                    </span>
                  )}
                </div>

                {request.status === 'PENDING' && (
                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => setConfirming(request)}
                      disabled={busyId === request.id}
                      className="flex items-center gap-2 rounded-xl bg-rose-500 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-rose-400 disabled:opacity-50"
                    >
                      <Trash2 size={14} />
                      Approve deletion
                    </button>
                    <button
                      type="button"
                      onClick={() => setRejecting(request)}
                      disabled={busyId === request.id}
                      className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-300 transition hover:bg-slate-700 disabled:opacity-50"
                    >
                      <XCircle size={14} />
                      Reject & restore
                    </button>
                  </div>
                )}
              </div>
            ))}

          {!isRequestTab(tab) &&
            deactivated.map((account) => (
              <div
                key={account.id}
                className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5 shadow-xl"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-black text-slate-100">
                        {account.fullName || 'Unnamed user'}
                      </span>
                      <span className="rounded-full bg-slate-800 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        {ROLE_LABEL[account.role] || account.role}
                      </span>
                      {account.isCompanyAdminUser && (
                        <span className="rounded-full bg-fuchsia-500/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-fuchsia-300">
                          Company admin
                        </span>
                      )}
                      {account.hasPendingDeletionRequest && (
                        <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-300">
                          Deletion requested
                        </span>
                      )}
                    </div>
                    <p className="mt-1 truncate text-xs text-slate-500">{account.email}</p>
                  </div>

                  <div className="text-right text-xs text-slate-500">
                    <p className="flex items-center justify-end gap-1.5">
                      <Clock size={12} />
                      {formatDate(account.deactivatedAt)}
                    </p>
                    <p className="mt-1">
                      {account.daysDeactivated != null
                        ? account.daysDeactivated === 0
                          ? 'today'
                          : account.daysDeactivated === 1
                          ? '1 day ago'
                          : `${account.daysDeactivated} days ago`
                        : '—'}
                    </p>
                  </div>
                </div>

                {account.companyName && (
                  <p className="mt-3 text-xs text-slate-500">
                    {account.companyName}
                    {account.recruiterStatus === false && ' · recruiter flag also off'}
                  </p>
                )}

                {account.deactivationReason && (
                  <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                      Reason given
                    </p>
                    <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-300">
                      {account.deactivationReason}
                    </p>
                  </div>
                )}

                {!account.hasPendingDeletionRequest && (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => setRestoring(account)}
                      disabled={busyId === account.id}
                      className="flex items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-emerald-400 transition hover:bg-emerald-500/20 hover:text-emerald-300 disabled:opacity-50"
                    >
                      <UserCheck size={14} />
                      Reactivate
                    </button>
                  </div>
                )}
              </div>
            ))}
        </div>

        {!loading && totalElements > 0 && (
          <Pager
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            onPage={setPage}
            disabled={loading}
          />
        )}
      </div>

      {confirming && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
          onClick={closeModals}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Approve account deletion"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-rose-500/30 bg-slate-900 p-6 shadow-2xl"
          >
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
                <AlertTriangle size={20} />
              </span>
              <div>
                <h3 className="text-base font-black tracking-tight text-slate-100">
                  Permanently delete this account?
                </h3>
                <p className="mt-1 text-sm text-slate-400">
                  {confirming.fullName || confirming.email}'s account, profile and everything
                  attached to it will be removed. They will be emailed at {confirming.email}.
                  This cannot be undone.
                </p>
              </div>
            </div>

            <label className="mt-5 block">
              <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                Type {CONFIRM_PHRASE} to confirm
              </span>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                disabled={busyId === confirming.id}
                autoComplete="off"
                placeholder={CONFIRM_PHRASE}
                className="mt-1.5 w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-600 outline-none transition focus:border-rose-500/50 disabled:opacity-60"
              />
            </label>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeModals}
                disabled={busyId === confirming.id}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() =>
                  run(
                    confirming.id,
                    () => approveAccountDeletionRequest(confirming.id),
                    `${confirming.email}'s account has been deleted and they have been emailed.`,
                  )
                }
                disabled={busyId === confirming.id || confirmText.trim().toUpperCase() !== CONFIRM_PHRASE}
                className="flex items-center gap-2 rounded-xl bg-rose-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-rose-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busyId === confirming.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                {busyId === confirming.id ? 'Deleting…' : 'Delete account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejecting && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
          onClick={closeModals}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Reject account deletion request"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl"
          >
            <h3 className="text-base font-black tracking-tight text-slate-100">
              Reject this deletion request?
            </h3>
            <p className="mt-1 text-sm text-slate-400">
              {rejecting.email}'s account is returned to active and they can sign in again.
              They are emailed your note.
            </p>

            <label className="mt-5 block">
              <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                Note to the requester (optional)
              </span>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                disabled={busyId === rejecting.id}
                rows={3}
                maxLength={1000}
                placeholder="Why the request is not being processed"
                className="mt-1.5 w-full resize-none rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-600 outline-none transition focus:border-cyan-400/50 disabled:opacity-60"
              />
            </label>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeModals}
                disabled={busyId === rejecting.id}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() =>
                  run(
                    rejecting.id,
                    () => rejectAccountDeletionRequest(rejecting.id, rejectNote),
                    `Request refused. ${rejecting.email}'s account has been restored.`,
                  )
                }
                disabled={busyId === rejecting.id}
                className="flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-cyan-400 disabled:opacity-50"
              >
                {busyId === rejecting.id && <Loader2 size={15} className="animate-spin" />}
                Reject & restore
              </button>
            </div>
          </div>
        </div>
      )}

      {restoring && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
          onClick={closeModals}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Reactivate account"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-emerald-500/30 bg-slate-900 p-6 shadow-2xl"
          >
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <UserCheck size={20} />
              </span>
              <div>
                <h3 className="text-base font-black tracking-tight text-slate-100">
                  Reactivate this account?
                </h3>
                <p className="mt-1 text-sm text-slate-400">
                  {restoring.fullName || restoring.email} will be able to sign in again
                  immediately{restoring.role === 'RECRUITER' ? ', and their recruiter flag is switched back on' : ''}.
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeModals}
                disabled={busyId === restoring.id}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() =>
                  run(
                    restoring.id,
                    () => reactivateDeactivatedAccount(restoring.id),
                    `${restoring.email}'s account has been reactivated.`,
                  )
                }
                disabled={busyId === restoring.id}
                className="flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-emerald-400 disabled:opacity-50"
              >
                {busyId === restoring.id && <Loader2 size={15} className="animate-spin" />}
                Reactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDeletionRequestsPage;
